import React from "react";
import { Card } from "./ui";
import { Crosshair, ListChecks, Target, FolderKanban } from "lucide-react";

const PRIORITY_QUADRANTS = [
  { key: "quick_win", color: "#10B981" },
  { key: "major_bet", color: "#2563EB" },
  { key: "fill_in", color: "#F59E0B" },
  { key: "thankless", color: "#EF4444" },
];

const KIND_ICONS = { goal: Target, project: FolderKanban, project_task: ListChecks, task: ListChecks };

// "What to work on next" — open work grouped by impact/effort quadrant, clickable.
export default function PrioritiesPanel({ priorities, t, lang, navigate, className = "mb-6" }) {
  const total = PRIORITY_QUADRANTS.reduce((n, q) => n + (priorities[q.key]?.length || 0), 0);
  return (
    <Card className={className} data-testid="priorities-panel">
      <div className="flex items-center gap-2 mb-1">
        <Crosshair className="w-4 h-4 text-text-secondary" />
        <h3 className="font-heading font-medium">{t("priorities_title")}</h3>
      </div>
      <p className="text-text-secondary text-sm mb-5">{t("priorities_subtitle")}</p>
      {total === 0 ? (
        <p className="text-sm text-text-secondary py-6 text-center">{t("priorities_empty")}</p>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3">
          {PRIORITY_QUADRANTS.map((q) => {
            const items = priorities[q.key] || [];
            return (
              <div key={q.key} className="rounded-xl border p-3 flex flex-col" data-testid={`priority-col-${q.key}`}
                style={{ borderColor: `${q.color}55`, background: `${q.color}0d` }}>
                <div className="flex items-center gap-2 mb-2.5">
                  <span className="w-2 h-2 rounded-full" style={{ backgroundColor: q.color }} />
                  <span className="text-xs font-semibold" style={{ color: q.color }}>{t(q.key)}</span>
                  <span className="text-[10px] text-text-secondary ml-auto">{items.length}</span>
                </div>
                <div className="space-y-1.5">
                  {items.map((it, i) => {
                    const Icon = KIND_ICONS[it.kind] || ListChecks;
                    return (
                      <button key={i} onClick={() => navigate(it.path)} data-testid={`priority-item-${q.key}-${i}`}
                        className="w-full flex items-start gap-2 px-2.5 py-2 rounded-lg bg-surface hover:bg-surface-hover text-left transition-colors">
                        <Icon className="w-3.5 h-3.5 mt-0.5 shrink-0 text-text-secondary" />
                        <span className="min-w-0 flex-1">
                          <span className="block text-xs font-medium truncate">{it.name}</span>
                          <span className="flex items-center gap-1.5 mt-0.5">
                            <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ backgroundColor: it.entity_color }} />
                            <span className="text-[10px] text-text-secondary truncate">{it.entity_name}</span>
                            {it.due_date && (
                              <span className={`text-[10px] ml-auto shrink-0 ${it.overdue_days > 0 ? "text-red-400 font-medium" : "text-text-secondary"}`}>
                                {it.overdue_days > 0 ? `${it.overdue_days}${lang === "fr" ? "j" : "d"}` : it.due_date.slice(5)}
                              </span>
                            )}
                          </span>
                        </span>
                      </button>
                    );
                  })}
                  {items.length === 0 && <span className="text-[11px] text-text-secondary">—</span>}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </Card>
  );
}
